import React, { useState, useEffect, useRef } from "react";
import logo from "../../../images/alaula-logo.jpg";
import { NavLink, useLocation } from "react-router-dom";
import { LuMenu, LuX, LuLogOut, LuSettings } from "react-icons/lu";
import storage from "../../../utils/storage";
import { DIR, LOCALE, isEnglish, t } from "../../../i18n";
import ThemeToggle from "../../ui/ThemeToggle";
import LanguageToggle from "../../ui/LanguageToggle";
import CompanySettingsModal from "../../Settings/CompanySettingsModal";
import { useAuthContext } from "../../../contexts/AuthContext";
import { prefetchPages } from "../../../routes/pages";
import { navItemsFor, PAGE_TITLES } from "../../Layout/navItems";

const NavbarOfDashborad = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { isOwner } = useAuthContext();
  const navItems = navItemsFor(isOwner);
  const location = useLocation(); // Track route changes
  const drawerRef = useRef(null);
  const menuButtonRef = useRef(null);

  const currentDate = new Date().toLocaleDateString(LOCALE, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const handleLogout = () => {
    localStorage.removeItem("token");
    storage.clearToken(); // the app stores its session under Bearer_token
    window.location.href = "/login";
  };

  // Warm the other pages' code while the browser is idle.
  useEffect(() => {
    prefetchPages();
  }, []);

  // Automatically close the drawer when the route changes
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location]);

  // Drawer behavior: lock page scroll, move focus in, trap Tab, close on Escape, restore focus.
  useEffect(() => {
    if (!isDrawerOpen) return undefined;
    const panel = drawerRef.current;
    const menuButton = menuButtonRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusable = () => [...panel.querySelectorAll("a[href], button:not([disabled])")];
    focusable()[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsDrawerOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      menuButton?.focus();
    };
  }, [isDrawerOpen]);

  // A page inside a section (an employee's report, an item's maintenance history) takes the section's title.
  const getPageTitle = () =>
    PAGE_TITLES[location.pathname] ||
    PAGE_TITLES[
      ["/reports", "/maintenance"].find((section) => location.pathname.startsWith(`${section}/`))
    ] ||
    t("لوحة التحكم");

  const focusRingOnBrand =
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

  return (
    <header className="card print:hidden">
      {/* Brand bar */}
      <div
        dir={DIR}
        className="rounded-2xl bg-gradient-to-l from-brand-700 to-brand-600 px-4 py-4 text-white shadow-card sm:px-6"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt={t("شركة العلى للمقاولات العامة المحدودة")}
              className="h-12 w-auto rounded-lg bg-white p-0.5 shadow-sm md:h-16"
            />
          </div>

          <div className="flex items-center gap-2">
            <LanguageToggle
              className={`inline-flex h-11 items-center justify-center rounded-xl px-2 text-xl hover:bg-white/15 ${focusRingOnBrand}`}
            />
            <ThemeToggle
              className={`inline-flex h-11 w-11 items-center justify-center rounded-xl text-xl hover:bg-white/15 ${focusRingOnBrand}`}
            />
            {isOwner && (
              <button
                type="button"
                onClick={() => setIsSettingsOpen(true)}
                aria-label={t("إعدادات الشركة")}
                title={t("إعدادات الشركة")}
                className={`inline-flex h-11 w-11 items-center justify-center rounded-xl text-xl hover:bg-white/15 ${focusRingOnBrand}`}
              >
                <LuSettings aria-hidden="true" />
              </button>
            )}
            <button
              ref={menuButtonRef}
              type="button"
              className={`inline-flex h-11 w-11 items-center justify-center rounded-xl text-2xl hover:bg-white/15 lg:hidden ${focusRingOnBrand}`}
              aria-label={t("فتح القائمة")}
              aria-haspopup="dialog"
              aria-expanded={isDrawerOpen}
              aria-controls="mobile-nav-drawer"
              onClick={() => setIsDrawerOpen(true)}
            >
              <LuMenu aria-hidden="true" />
            </button>
            {/* Logout (desktop); the drawer has its own on mobile */}
            <button
              type="button"
              onClick={handleLogout}
              className={`hidden items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 font-medium ring-1 ring-white/25 hover:bg-card hover:text-brand-700 lg:inline-flex ${focusRingOnBrand}`}
            >
              <LuLogOut aria-hidden="true" className="text-lg" />
              <span>{t("تسجيل الخروج")}</span>
            </button>
          </div>
        </div>

        {/* Title and date */}
        <div className="mt-4 flex items-end justify-between gap-3">
          <h1 className="text-xl font-bold">{getPageTitle()}</h1>
          <p className="text-sm text-white/85">{currentDate}</p>
        </div>
      </div>

      {/* Mobile drawer */}
      {isDrawerOpen && (
        <div dir={DIR} className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 animate-fade-in bg-black/60 backdrop-blur-[2px]"
            onClick={() => setIsDrawerOpen(false)}
            aria-hidden="true"
          />
          <div
            id="mobile-nav-drawer"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={t("القائمة الرئيسية")}
            className={`relative flex h-full w-72 max-w-[85vw] ${isEnglish ? "animate-drawer-in-right" : "animate-drawer-in-left"} flex-col bg-card p-5 shadow-pop`}
          >
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-brand-700">{t("القائمة")}</span>
              <button
                type="button"
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-2xl text-brand-700 hover:bg-brand-50"
                aria-label={t("إغلاق القائمة")}
                onClick={() => setIsDrawerOpen(false)}
              >
                <LuX aria-hidden="true" />
              </button>
            </div>

            <nav aria-label={t("التنقل الرئيسي")} className="mt-4 flex-1 space-y-1 overflow-y-auto">
              {navItems.map(({ path, label, Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-4 py-3 font-medium transition-colors ${
                      isActive
                        ? "bg-brand-600 text-white shadow-sm"
                        : "text-slate-700 hover:bg-brand-50 hover:text-brand-700"
                    }`
                  }
                >
                  <Icon aria-hidden="true" className="shrink-0 text-xl" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </nav>

            <button
              type="button"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 font-medium text-white hover:bg-red-700"
              onClick={handleLogout}
            >
              <LuLogOut aria-hidden="true" className="text-lg" />
              <span>{t("تسجيل الخروج")}</span>
            </button>
          </div>
        </div>
      )}
      {isSettingsOpen && <CompanySettingsModal onClose={() => setIsSettingsOpen(false)} />}
    </header>
  );
};

export default NavbarOfDashborad;
